import { easybackClient } from './api'
import { cacheService, CACHE_TTL } from './cacheService'
import type { StudySession, Subject, Topic, Lesson } from '@/types/index'

export interface StudySessionWithDetails extends StudySession {
  lesson_name?: string
  topic_name?: string
  subject_name?: string
  subject_id?: number
  content_type?: Lesson['content_type']
}

export interface SessionFilters {
  start_date?: string
  end_date?: string
  subject_id?: number
  topic_id?: number
  lesson_id?: number
}

interface SessionsWithMetadata {
  sessions: StudySession[]
  min_date?: string
  max_date?: string
}

export interface SessionStats {
  total_sessions: number
  total_duration_minutes: number
  total_duration_hours: number
  average_session_duration: number
  completed_sessions: number
  completion_rate: number
  longest_session: number
  current_streak: number
  longest_streak: number
  today_duration: number
  week_duration: number
  month_duration: number
}

export interface StudySessionInput {
  user_id: number
  lesson_id: number
  notes?: string
  context?: 'lesson' | 'quiz' | 'dashboard' | 'practice' | 'other'
}

export interface StudySessionUpdate {
  duration?: number
  completed?: boolean
  end_time?: string
  notes?: string
  context?: 'lesson' | 'quiz' | 'dashboard' | 'practice' | 'other'
}

const cachedStartIsBeforeOrEqual = (cachedDate: string, requestedDate: string): boolean => {
  return new Date(cachedDate) <= new Date(requestedDate)
}

const cachedEndIsAfterOrEqual = (cachedDate: string, requestedDate: string): boolean => {
  return new Date(cachedDate) >= new Date(requestedDate)
}

const filterSessionsByDate = (sessions: StudySession[], startDate?: string, endDate?: string): StudySession[] => {
  if (!startDate && !endDate) return sessions

  return sessions.filter((s) => {
    if (!s.start_time) return false

    if (startDate && new Date(s.start_time) < new Date(startDate)) return false
    if (endDate && new Date(s.start_time) > new Date(endDate)) return false

    return true
  })
}

export const toLocalDateKey = (d: Date): string => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export const studySessionService = {
  logSession: async (data: StudySessionInput) => {
    const result = await easybackClient.insert<StudySession>('study_sessions', {
      user_id: data.user_id,
      lesson_id: data.lesson_id,
      start_time: new Date().toISOString(),
      duration: 0,
      completed: false,
      notes: data.notes,
      context: data.context || 'other'
    } as any)
    studySessionService.invalidateSessionCache(data.user_id)
    return result.data?.[0]
  },

  getActiveSession: async (userId: number) => {
    const result = await easybackClient.list<StudySession>('study_sessions', {
      filter: {
        user_id: { eq: userId },
        completed: { eq: false }
      },
      sort: { start_time: 'desc' },
      limit: 1
    })
    return result.data?.[0] || null
  },

  getSession: async (sessionId: number): Promise<StudySession | null> => {
    const cacheKey = `study_session_${sessionId}`
    const cached = cacheService.get<StudySession>(cacheKey)
    if (cached) return cached

    const result = await easybackClient.get<StudySession>('study_sessions', sessionId)
    const session = result.data?.[0] || null

    if (session) {
      cacheService.set(cacheKey, session, CACHE_TTL.MEDIUM)
    }

    return session
  },

  endSession: async (sessionId: number, data: StudySessionUpdate) => {
    const sessionResult = await easybackClient.get<StudySession>('study_sessions', sessionId)
    const userId = sessionResult.data?.[0]?.user_id

    const updateData = {
      duration: data.duration,
      completed: data.completed,
      end_time: data.end_time || new Date().toISOString(),
      notes: data.notes
    }
    const result = await easybackClient.update<StudySession>('study_sessions', sessionId, updateData as any)

    if (userId) {
      studySessionService.invalidateSessionCache(userId)
    }
    return result.data?.[0]
  },

  updateSession: async (sessionId: number, data: StudySessionUpdate) => {
    const sessionResult = await easybackClient.get<StudySession>('study_sessions', sessionId)
    const userId = sessionResult.data?.[0]?.user_id

    const updateData: Record<string, unknown> = {}
    if (data.duration !== undefined) updateData.duration = data.duration
    if (data.completed !== undefined) updateData.completed = data.completed
    if (data.end_time) updateData.end_time = data.end_time
    if (data.notes !== undefined) updateData.notes = data.notes
    if (data.context) updateData.context = data.context

    const result = await easybackClient.update<StudySession>('study_sessions', sessionId, updateData as any)

    if (userId) {
      studySessionService.invalidateSessionCache(userId)
    }
    return result.data?.[0]
  },

  updateSessionDuration: async (sessionId: number, duration: number) => {
    const sessionResult = await easybackClient.get<StudySession>('study_sessions', sessionId)
    const userId = sessionResult.data?.[0]?.user_id

    const result = await easybackClient.update<StudySession>('study_sessions', sessionId, { duration } as any)

    if (userId) {
      studySessionService.invalidateSessionCache(userId)
    }
    return result.data?.[0]
  },

  updateSnapshotCount: async (sessionId: number) => {
    const sessionResult = await easybackClient.get<StudySession>('study_sessions', sessionId)
    const userId = sessionResult.data?.[0]?.user_id
    const currentSnapshotCount = sessionResult.data?.[0]?.snapshot_count || 0

    const result = await easybackClient.update<StudySession>('study_sessions', sessionId, {
      snapshot_count: currentSnapshotCount + 1,
      last_snapshot_at: new Date().toISOString()
    } as any)

    if (userId) {
      studySessionService.invalidateSessionCache(userId)
    }
    return result.data?.[0]
  },

  invalidateSessionCache: (userId: number): void => {
    cacheService.invalidatePattern(`user_sessions_${userId}_`)
    cacheService.invalidatePattern(`user_sessions_details_${userId}_`)
    cacheService.invalidatePattern(`user_sessions_all_${userId}`)
    cacheService.invalidatePattern(`study_time_by_`)
  },

  invalidateContentCache: (): void => {
    cacheService.invalidatePattern(`lessons_by_ids_`)
    cacheService.invalidatePattern(`topics_by_ids_`)
    cacheService.invalidatePattern(`subjects_all`)
  },

  getUserSessions: async (userId: number, filters?: SessionFilters) => {
    const filterKey = JSON.stringify(filters || {})
    const cacheKey = `user_sessions_${userId}_${filterKey}`

    const hasDateFilters = filters?.start_date || filters?.end_date
    const hasNonDateFilters = filters?.subject_id || filters?.topic_id || filters?.lesson_id

    if (hasDateFilters && !hasNonDateFilters) {
      const allSessionsKey = `user_sessions_all_${userId}`
      const allCached = cacheService.get<SessionsWithMetadata>(allSessionsKey)

      if (allCached && allCached.sessions.length > 0) {
        const requestedStart = filters.start_date
        const requestedEnd = filters.end_date
        const cachedMin = allCached.min_date
        const cachedMax = allCached.max_date

        let canReuseCached = false

        if (!requestedStart && !requestedEnd) {
          canReuseCached = true
        } else if (requestedStart && !requestedEnd && cachedMin && cachedStartIsBeforeOrEqual(cachedMin, requestedStart)) {
          canReuseCached = true
        } else if (!requestedStart && requestedEnd && cachedMax && cachedEndIsAfterOrEqual(cachedMax, requestedEnd)) {
          canReuseCached = true
        } else if (requestedStart && requestedEnd && cachedMin && cachedMax) {
          canReuseCached = cachedStartIsBeforeOrEqual(cachedMin, requestedStart) && cachedEndIsAfterOrEqual(cachedMax, requestedEnd)
        }

        if (canReuseCached) {
          const filtered = filterSessionsByDate(allCached.sessions, requestedStart, requestedEnd)
          cacheService.set(cacheKey, filtered, CACHE_TTL.SHORT)
          return filtered
        }
      }
    }

    const cached = cacheService.get<StudySession[]>(cacheKey)
    if (cached) return cached

    const filterConditions: Record<string, unknown> = {
      user_id: { eq: userId }
    }

    if (filters?.start_date || filters?.end_date) {
      const dateFilter: Record<string, unknown> = {}
      if (filters.start_date) {
        dateFilter.gte = filters.start_date
      }
      if (filters.end_date) {
        dateFilter.lte = filters.end_date
      }
      filterConditions.start_time = dateFilter
    }

    const result = await easybackClient.list<StudySession>('study_sessions', {
      filter: filterConditions,
      sort: { start_time: 'desc' },
      limit: 1000
    })
    const data = result.data || []
    cacheService.set(cacheKey, data, CACHE_TTL.SHORT)

    if (!hasDateFilters && !hasNonDateFilters) {
      const sessionsWithDates = data.filter((s) => s.start_time).sort((a, b) => a.start_time!.localeCompare(b.start_time!))
      const minDate = sessionsWithDates[0]?.start_time
      const maxDate = sessionsWithDates[sessionsWithDates.length - 1]?.start_time

      cacheService.set(
        `user_sessions_all_${userId}`,
        { sessions: data, min_date: minDate, max_date: maxDate },
        CACHE_TTL.SHORT
      )
    }

    return data
  },

  getUserSessionsWithDetails: async (userId: number, filters?: SessionFilters): Promise<StudySessionWithDetails[]> => {
    const filterKey = JSON.stringify(filters || {})
    const cacheKey = `user_sessions_details_${userId}_${filterKey}`
    const cached = cacheService.get<StudySessionWithDetails[]>(cacheKey)
    if (cached) return cached

    const sessions = await studySessionService.getUserSessions(userId, filters)
    const lessonIds = [...new Set(sessions.map((s) => s.lesson_id))]

    if (lessonIds.length === 0) {
      cacheService.set(cacheKey, [], CACHE_TTL.SHORT)
      return []
    }

    const lessonsKey = `lessons_by_ids_${lessonIds.join(',')}`
    const lessonsCache = cacheService.get<Lesson[]>(lessonsKey)

    const topicsKey = `topics_by_ids_${lessonIds.join(',')}`
    const topicsCache = cacheService.get<Topic[]>(topicsKey)

    const subjectsKey = `subjects_all`
    const subjectsCache = cacheService.get<Subject[]>(subjectsKey)

    let lessons = lessonsCache
    let topics = topicsCache
    let subjects = subjectsCache

    if (!lessons || !topics || !subjects) {
      const fetches: Array<Promise<unknown>> = []

      if (!lessons) {
        fetches.push(
          easybackClient.list<Lesson>('lessons', {
            filter: { id: { in: lessonIds } },
            limit: 1000,
            fields: ['id', 'name', 'topic_id', 'content_type']
          }).then((r) => {
            lessons = r.data || []
            cacheService.set(lessonsKey, lessons, CACHE_TTL.MEDIUM)
          })
        )
      }

      if (!topics) {
        fetches.push(
          easybackClient.list<Topic>('topics', {
            limit: 1000
          }).then((r) => {
            topics = r.data || []
            cacheService.set(topicsKey, topics, CACHE_TTL.MEDIUM)
          })
        )
      }

      if (!subjects) {
        fetches.push(
          easybackClient.list<Subject>('subjects', {
            limit: 100
          }).then((r) => {
            subjects = r.data || []
            cacheService.set(subjectsKey, subjects, CACHE_TTL.EXTREMELY_LONG)
          })
        )
      }

      if (fetches.length > 0) {
        await Promise.all(fetches)
      }
    }

    const result = sessions.map((session) => {
      const lesson = lessons?.find((l) => l.id === session.lesson_id)
      const topic = lesson ? topics?.find((t) => t.id === lesson.topic_id) : undefined
      const subject = topic ? subjects?.find((s) => s.id === topic.subject_id) : undefined

      return {
        ...session,
        lesson_name: lesson?.name,
        topic_name: topic?.name,
        subject_name: subject?.name,
        subject_id: subject?.id,
        content_type: lesson?.content_type
      }
    })

    cacheService.set(cacheKey, result, CACHE_TTL.SHORT)
    return result
  },

  getSessionStats: async (userId: number): Promise<SessionStats> => {
    const now = new Date()
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString()
    const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString()
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()

    const allSessions = await studySessionService.getUserSessions(userId)

    const sumSince = (isoStart: string) => allSessions
      .filter((s) => s.start_time && s.start_time >= isoStart)
      .reduce((sum, s) => sum + (s.duration || 0), 0)

    const totalDuration = allSessions.reduce((sum, s) => sum + (s.duration || 0), 0)
    const completedSessions = allSessions.filter((s) => s.completed).length
    const longestSession = Math.max(...allSessions.map((s) => s.duration || 0), 0)
    const avgDuration = allSessions.length > 0 ? totalDuration / allSessions.length : 0

    const todayDuration = sumSince(todayStart)
    const weekDuration = sumSince(weekStart)
    const monthDuration = sumSince(monthStart)

    const streaks = studySessionService.calculateStreaks(allSessions)

    const result = {
      total_sessions: allSessions.length,
      total_duration_minutes: totalDuration,
      total_duration_hours: Math.round(totalDuration / 60),
      average_session_duration: Math.round(avgDuration),
      completed_sessions: completedSessions,
      completion_rate: allSessions.length > 0 ? Math.round((completedSessions / allSessions.length) * 100) : 0,
      longest_session: longestSession,
      current_streak: streaks.current,
      longest_streak: streaks.longest,
      today_duration: todayDuration,
      week_duration: weekDuration,
      month_duration: monthDuration
    }

    return result
  },

  getStudyTimeByLesson: async (userId: number): Promise<Map<number, number>> => {
    const sessions = await studySessionService.getUserSessions(userId)

    const byLesson = new Map<number, number>()
    sessions.forEach((session) => {
      if (session.lesson_id == null) return
      byLesson.set(session.lesson_id, (byLesson.get(session.lesson_id) || 0) + (session.duration || 0))
    })

    return byLesson
  },

  calculateStreaks: (sessions: StudySession[]) => {
    const sessionsByDate = new Map<string, boolean>()

    sessions.forEach((session) => {
      if (session.completed && session.start_time) {
        sessionsByDate.set(toLocalDateKey(new Date(session.start_time)), true)
      }
    })

    const sortedDates = Array.from(sessionsByDate.keys()).sort()
    let currentStreak = 0
    let longestStreak = 0
    let tempStreak = 0

    let checkDate = new Date()

    for (let i = 0; i < 365; i++) {
      const dateStr = toLocalDateKey(checkDate)
      if (sessionsByDate.has(dateStr)) {
        if (i === 0) currentStreak = 1
        else if (currentStreak > 0) currentStreak++
        tempStreak++
      } else {
        if (i > 0) break
        tempStreak = 0
      }
      checkDate.setDate(checkDate.getDate() - 1)
    }

    tempStreak = 0
    sortedDates.forEach((date, index) => {
      if (index === 0) {
        tempStreak = 1
      } else {
        const prevDate = new Date(sortedDates[index - 1])
        const currDate = new Date(date)
        const diffDays = Math.abs((currDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24))

        if (diffDays === 1) {
          tempStreak++
        } else {
          longestStreak = Math.max(longestStreak, tempStreak)
          tempStreak = 1
        }
      }
    })
    longestStreak = Math.max(longestStreak, tempStreak)

    return { current: currentStreak, longest: longestStreak }
  },

  getSessionsBySubject: async (userId: number, subjectId: number) => {
    const subjectsSessions = await studySessionService.getUserSessionsWithDetails(userId)
    return subjectsSessions.filter((s) => s.subject_id === subjectId)
  },

  getStudyTimeByDay: async (userId: number, days: number = 30) => {
    const endDate = new Date()
    const startDate = new Date(endDate.getTime() - (days - 1) * 24 * 60 * 60 * 1000)
    const startDateStr = startDate.toISOString().split('T')[0]

    const sessions = await studySessionService.getUserSessions(userId, { start_date: startDateStr })

    const timeByDay = new Map<string, number>()

    for (let i = 0; i < days; i++) {
      const date = new Date(startDate)
      date.setDate(date.getDate() + i)
      timeByDay.set(toLocalDateKey(date), 0)
    }

    sessions.forEach((session) => {
      const dateStr = toLocalDateKey(new Date(session.start_time))
      if (timeByDay.has(dateStr)) {
        timeByDay.set(dateStr, (timeByDay.get(dateStr) || 0) + (session.duration || 0))
      }
    })

    return Array.from(timeByDay.entries()).map(([date, minutes]) => ({
      date,
      minutes,
      hours: Math.round(minutes / 60)
    }))
  },

  getStudyTimeBySubject: async (userId: number) => {
    const sessions = await studySessionService.getUserSessionsWithDetails(userId)

    const timeBySubject = new Map<string, number>()

    sessions.forEach((session) => {
      const subjectName = session.subject_name || 'Diğer'
      timeBySubject.set(subjectName, (timeBySubject.get(subjectName) || 0) + (session.duration || 0))
    })

    return Array.from(timeBySubject.entries()).map(([subject, minutes]) => ({
      subject,
      minutes,
      hours: Math.round(minutes / 60)
    })).sort((a, b) => b.minutes - a.minutes)
  }
}
